import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { AdjustmentFilter } from '@pixi/filter-adjustment';
import { Container, useTick } from '@pixi/react';
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * If the animation calls for an adjustment filter, ensure it exists when needed.
 * The assumption is that the filters array will only ever contain one item, an AdjustmentFilter.
 */
function ensureAdjustmentFilterExists(filters) {
    if (!filters.length) {
        const newAdjustmentFilter = new AdjustmentFilter();
        filters.push(newAdjustmentFilter);
        return newAdjustmentFilter;
    }
    return filters[0];
}
const ANIMATION_MODIFIERS = {
    x: (container, filters, value) => {
        container.x = value;
    },
    y: (container, filters, value) => {
        container.y = value;
    },
    angle: (container, filters, value) => {
        container.angle = value;
    },
    scale: (container, filters, value) => {
        // TODO: See if there is a way to only set the scale (`countainer.scale = ...` does not work!)
        container.setTransform?.(container.x, container.y, value, value, container.rotation, 0, 0, 0, 0);
    },
    gamma: (container, filters, value) => {
        ensureAdjustmentFilterExists(filters).gamma = value;
    },
    contrast: (container, filters, value) => {
        ensureAdjustmentFilterExists(filters).contrast = value;
    }
};
function getValueAtTime(tween, elapsedMs) {
    const { startValue, endValue, durationMs, fn } = tween;
    if (elapsedMs <= 0) {
        return startValue;
    }
    if (elapsedMs >= durationMs) {
        return endValue;
    }
    if (!fn) {
        // if fn is not provided, just return start value
        return startValue;
    }
    const tweenMagnitude = Math.abs(endValue - startValue);
    return startValue + (endValue > startValue ? 1 : -1) * fn(elapsedMs / durationMs) * tweenMagnitude;
}
export function getContainerEndState(allAnimationSeries) {
    const containerEndState = {};
    Object.entries(allAnimationSeries).forEach(([tweenType, tweens]) => {
        let tweenSetDurationMs = 0;
        tweens.forEach((tween) => {
            tweenSetDurationMs += tween.durationMs;
            const tweenValueSample = containerEndState[tweenType];
            if (!tweenValueSample || tweenSetDurationMs > tweenValueSample.timestampMs) {
                // end state for this tween type wasn't set yet, or found a later tween state
                containerEndState[tweenType] = { value: tween.endValue, timestampMs: tweenSetDurationMs };
            }
        });
    });
    return containerEndState;
}
function applyContainerEndState(container, filters, allAnimationSeries) {
    const containerEndState = getContainerEndState(allAnimationSeries);
    Object.entries(containerEndState).forEach(([tweenType, tweenValueSample]) => {
        ANIMATION_MODIFIERS[tweenType](container, filters, tweenValueSample.value);
    });
}
function getActiveTween(totalElapsedMs, tweens) {
    let tweensDurationMs = 0;
    for (let i = 0; i < tweens.length; i++) {
        const tween = tweens[i];
        if (totalElapsedMs < tweensDurationMs + tween.durationMs) {
            return [tween, tweensDurationMs];
        }
        tweensDurationMs += tween.durationMs;
    }
}
function updateAnimation(container, filters, startTimestampMs, nowMs, allAnimationSeries) {
    const totalElapsedMs = nowMs - startTimestampMs;
    Object.entries(allAnimationSeries).forEach(([tweenType, tweens]) => {
        const activeTweenAndRelativeStartMs = getActiveTween(totalElapsedMs, tweens);
        if (!activeTweenAndRelativeStartMs) {
            return;
        }
        const [activeTween, relativeStartMs] = activeTweenAndRelativeStartMs;
        const tweenElapsedMs = totalElapsedMs - relativeStartMs;
        const valueNow = getValueAtTime(activeTween, tweenElapsedMs);
        ANIMATION_MODIFIERS[tweenType](container, filters, valueNow);
    });
}
/**
 * Container that can animate a combination of properties over time, including filter effects.
 * For performance reasons, filters are removed when the provided series complete, but other properties
 * (angle and scale for example) will remain at their final states when the animation series complete.
 */
export const AnimationContainer = ({ startTimestampMs, animationSet, onAnimationDone, children }) => {
    const [isAnimating, setIsAnimating] = useState(true);
    const containerRef = useRef(null);
    // Note that when populated, the filters array should only contain a single element, an AdjustmentFilter:
    const filtersRef = useRef([]);
    const animate = useCallback(() => {
        if (!containerRef.current) {
            return;
        }
        const nowMs = performance.now();
        const elapsedMs = nowMs - startTimestampMs;
        if (elapsedMs >= animationSet.totalDurationMs) {
            applyContainerEndState(containerRef.current, filtersRef.current, animationSet.animations);
            // clear out filters array when animation is done to help performance:
            filtersRef.current.splice(0);
            onAnimationDone?.();
            setIsAnimating(false);
            return;
        }
        updateAnimation(containerRef.current, filtersRef.current, startTimestampMs, nowMs, animationSet.animations);
    }, [animationSet.animations, animationSet.totalDurationMs, onAnimationDone, startTimestampMs]);
    useEffect(() => {
        setIsAnimating(true);
    }, [startTimestampMs]);
    useTick(animate, isAnimating);
    return (_jsx(Container, { ref: containerRef, filters: filtersRef.current, children: children }));
};
